import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { GraduationCap } from "lucide-react";
import { api } from "@/lib/api";
import { usePageTitle } from "@/lib/title";
import type { Enrollment } from "@/types";
import { Card, CardBody } from "@/components/ui/Card";
import { Badge, statusTone } from "@/components/ui/Badge";
import { FullPageSpinner } from "@/components/ui/Spinner";
import { EmptyState } from "@/components/ui/EmptyState";

export function MyLearningPage() {
  usePageTitle("My Learning");
  const { data: enrollments, isLoading } = useQuery({
    queryKey: ["enrollments", "me"],
    queryFn: () => api.get<Enrollment[]>("/api/v1.0/enrollments/me"),
  });

  if (isLoading) return <FullPageSpinner />;

  return (
    <div className="flex flex-col gap-6">
      <p className="-mt-3 text-sm text-slate-500">Track your progress across enrolled courses.</p>

      {!enrollments?.length ? (
        <EmptyState
          icon={GraduationCap}
          title="No enrollments yet"
          description="Browse the catalog to find your first course."
          action={
            <Link to="/courses" className="text-sm font-medium text-brand-600 hover:underline">
              Browse courses
            </Link>
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {enrollments.map((e) => (
            <Link key={e.id} to={`/courses/${e.course_id}`}>
              <Card className="h-full hover:shadow-md">
                <CardBody>
                  <div className="mb-2 flex items-center justify-between">
                    <Badge tone={statusTone(e.status)}>{e.status}</Badge>
                    <span className="text-xs text-slate-400">{new Date(e.enrolled_at).toLocaleDateString()}</span>
                  </div>
                  <h3 className="font-semibold text-slate-900">{e.course_title}</h3>
                  <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full bg-brand-600" style={{ width: `${e.progress_percent}%` }} />
                  </div>
                  <p className="mt-1 text-xs text-slate-500">{e.progress_percent}% complete</p>
                </CardBody>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
